import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { config } from '@/config/env';
import { STORAGE_KEYS } from '@/config/constants';
import { logger } from '@/utils/logger';
import { APIError } from '@/utils/errors';

/**
 * API client configuration
 */
const apiConfig: AxiosRequestConfig = {
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  // withCredentials: false by default - JWT tokens are sent in headers, not cookies
  // Only enable for specific endpoints that need session cookies (if any)
  headers: {
    'Content-Type': 'application/json',
  },
};

/**
 * Create Axios instance
 */
export const apiClient: AxiosInstance = axios.create(apiConfig);

const shouldRedactPayload = (url?: string): boolean => {
  return Boolean(url && url.includes('/api/onboarding/export/'));
};

/**
 * Request interceptor
 * Adds authentication token to requests
 */
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    logger.apiRequest(
      config.method?.toUpperCase() || 'GET',
      config.url || '',
      shouldRedactPayload(config.url) ? '[redacted export payload]' : config.data
    );

    return config;
  },
  (error: AxiosError) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Handles responses and errors globally
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    logger.apiResponse(
      response.config.method?.toUpperCase() || 'GET',
      response.config.url || '',
      response.status,
      shouldRedactPayload(response.config.url) ? '[redacted export payload]' : response.data
    );

    return response;
  },
  (error: AxiosError) => {
    // Handle error responses
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as any;
      const message = data?.message || data?.error || 'An error occurred';

      logger.error(
        `API Error ${status}:`,
        message,
        shouldRedactPayload(error.config?.url) ? '[redacted export payload]' : error.response.data
      );

      // Handle specific status codes
      switch (status) {
        case 401:
          // Unauthorized - don't automatically clear token
          // Let the component/route handle auth redirects
          logger.warn('Unauthorized:', message);

          // Optionally update Zustand store to mark as unauthenticated
          // This allows ProtectedRoute to handle the redirect
          // import { useStore } from '@/stores';
          // useStore.setState((state) => ({
          //   auth: { ...state.auth, isAuthenticated: false }
          // }));
          break;

        case 403:
          // Forbidden
          logger.warn('Access forbidden:', message);
          break;

        case 404:
          // Not found
          logger.warn('Resource not found:', message);
          break;

        case 429:
          // Rate limit
          logger.warn('Rate limit exceeded:', message);
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          logger.error('Server error:', message);
          break;
      }

      // Throw custom API error
      throw new APIError(message, status, data?.code, data);
    } else if (error.request) {
      // Request made but no response received
      logger.error('No response received:', error.request);
      throw new APIError('Network error - no response from server', 0);
    } else {
      // Error setting up the request
      logger.error('Request setup error:', error.message);
      throw new APIError(error.message, 0);
    }
  }
);

/**
 * API request wrapper with type safety
 */
export const api = {
  /**
   * GET request
   */
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.get<T>(url, config).then((response) => response.data);
  },

  /**
   * POST request
   */
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.post<T>(url, data, config).then((response) => response.data);
  },

  /**
   * PUT request
   */
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.put<T>(url, data, config).then((response) => response.data);
  },

  /**
   * PATCH request
   */
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.patch<T>(url, data, config).then((response) => response.data);
  },

  /**
   * DELETE request
   */
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.delete<T>(url, config).then((response) => response.data);
  },
};

/**
 * Set authentication token
 */
export const setAuthToken = (token: string | null): void => {
  if (token) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

/**
 * Get current authentication token
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Clear authentication
 */
export const clearAuth = (): void => {
  setAuthToken(null);
};
